import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Send, Save, Sparkles, Paperclip, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useCommunications, useTemplates } from './useCommunications';
import { useEmailConfig } from '@/hooks/useEmailConfig';
import { CommunicationChannel, CandidateContext, JobContext, CommunicationTemplate, ReplyContext } from './types';
import RichTextEditor from '@/components/ui/rich-text-editor';

interface ComposeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidateId: string;
  candidate: CandidateContext;
  job?: JobContext | null;
  channel: CommunicationChannel;
  replyTo?: ReplyContext | null;
  onSent?: () => void;
}

export function ComposeDialog({
  open,
  onOpenChange,
  candidateId,
  candidate,
  job,
  channel,
  replyTo,
  onSent,
}: ComposeDialogProps) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [mode, setMode] = useState<'compose' | 'ai'>('compose');
  const [sendViaResend, setSendViaResend] = useState(true);

  const { toast } = useToast();
  const { createCommunication } = useCommunications(candidateId);
  const { templates } = useTemplates(channel);
  const { primaryConfig, emailConfigs, loading: emailConfigLoading } = useEmailConfig();

  // Reset sendViaResend based on whether user has email config
  useEffect(() => {
    if (!emailConfigLoading && !primaryConfig) {
      setSendViaResend(false);
    }
  }, [emailConfigLoading, primaryConfig]);

  // Pre-fill subject when replying
  useEffect(() => {
    if (replyTo?.subject) {
      setSubject(replyTo.subject);
    }
  }, [replyTo]);

  const getChannelTitle = () => {
    if (replyTo) {
      return 'Reply to Email';
    }
    switch (channel) {
      case 'email':
        return 'Compose Email';
      case 'phone':
        return 'Initiate Call';
      case 'whatsapp':
        return 'Send WhatsApp Message';
      case 'sms':
        return 'Send SMS';
      case 'note':
        return 'Add Note';
      default:
        return 'Compose Message';
    }
  };

  const getChannelDescription = () => {
    switch (channel) {
      case 'email':
        return 'Compose and send an email';
      case 'phone':
        return `Call ${candidate.phone} - calls will be recorded and transcribed`;
      case 'sms':
        return `Send SMS to ${candidate.phone}`;
      case 'whatsapp':
        return `Send WhatsApp message to ${candidate.phone}`;
      default:
        return 'Create a communication record';
    }
  };

  const applyTemplate = (template: CommunicationTemplate) => {
    let processedBody = template.body;
    let processedSubject = template.subject || '';

    // Replace variables
    const variables: Record<string, string> = {
      firstName: candidate.firstName || '',
      lastName: candidate.lastName || '',
      skills: candidate.skills?.join(', ') || '',
      jobTitle: job?.title || '',
      clientName: job?.clientName || '',
      location: job?.location || candidate.city || '',
      lastCompany: candidate.lastCompany || '',
      recruiterName: 'Recruiter',
      companyName: 'Company',
    };

    template.variables.forEach((variable) => {
      const regex = new RegExp(`{{${variable}}}`, 'g');
      processedBody = processedBody.replace(regex, variables[variable] || '');
      processedSubject = processedSubject.replace(regex, variables[variable] || '');
    });

    setBody(processedBody);
    if (processedSubject) {
      setSubject(processedSubject);
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = templates.find((t) => t.id === templateId);
    if (template) {
      applyTemplate(template);
    }
  };

  const generateAIDraft = async () => {
    try {
      setGenerating(true);

      const { data, error } = await supabase.functions.invoke('generate-communication-draft', {
        body: {
          candidate: {
            firstName: candidate.firstName,
            lastName: candidate.lastName,
            skills: candidate.skills,
            currentTitle: candidate.currentTitle,
            city: candidate.city,
          },
          job: job
            ? {
                title: job.title,
                clientName: job.clientName,
                location: job.location,
                description: job.description,
              }
            : null,
          channel,
          purpose: 'outreach',
        },
      });

      if (error) throw error;

      if (data?.subject) setSubject(data.subject);
      if (data?.body) setBody(data.body);

      toast({
        title: 'Draft generated',
        description: 'AI has generated a draft message. Feel free to edit it.',
      });
    } catch (error) {
      console.error('Error generating draft:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate AI draft',
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setAttachments((prev) => [...prev, ...files]);
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async (asDraft: boolean = false) => {
    if (!body.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a message',
        variant: 'destructive',
      });
      return;
    }

    // Validate email recipient for email channel
    if (channel === 'email' && sendViaResend && !candidate.email) {
      toast({
        title: 'Error',
        description: 'Candidate does not have an email address',
        variant: 'destructive',
      });
      return;
    }

    // Validate phone for SMS/WhatsApp/call channels
    if ((channel === 'sms' || channel === 'phone' || channel === 'whatsapp') && !candidate.phone) {
      toast({
        title: 'Error',
        description: 'Candidate does not have a phone number',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSending(true);

      // For SMS, WhatsApp, and voice, send directly via Twilio
      if (channel === 'sms' && !asDraft) {
        const { data, error } = await supabase.functions.invoke('send-sms', {
          body: {
            candidateId,
            to: candidate.phone,
            body,
            jobId: job?.id,
          },
        });

        if (error) throw error;
        
        // Check for error in response data (edge function returned 500)
        if (data?.error) {
          throw new Error(data.error);
        }
        
        if (!data?.success) {
          throw new Error('Failed to send SMS - no confirmation from Twilio');
        }

        toast({
          title: 'SMS sent',
          description: `SMS sent successfully to ${candidate.phone}`,
        });

        onSent?.();
        onOpenChange(false);
        return;
      }

      if (channel === 'whatsapp' && !asDraft) {
        const { data, error } = await supabase.functions.invoke('send-whatsapp', {
          body: {
            candidateId,
            to: candidate.phone,
            body,
            jobId: job?.id,
          },
        });

        if (error) throw error;
        
        // Check for error in response data (edge function returned 500)
        if (data?.error) {
          throw new Error(data.error);
        }
        
        if (!data?.success) {
          throw new Error('Failed to send WhatsApp - no confirmation from Twilio');
        }

        toast({
          title: 'WhatsApp sent',
          description: `WhatsApp message sent successfully to ${candidate.phone}`,
        });

        onSent?.();
        onOpenChange(false);
        return;
      }

      if (channel === 'phone' && !asDraft) {
        const { data, error } = await supabase.functions.invoke('initiate-call', {
          body: {
            candidateId,
            to: candidate.phone,
            record: true,
            jobId: job?.id,
          },
        });

        if (error) throw error;
        
        // Check for error in response data
        if (data?.error) {
          throw new Error(data.error);
        }

        toast({
          title: 'Call initiated',
          description: `Calling ${candidate.phone}...`,
        });

        onSent?.();
        onOpenChange(false);
        return;
      }

      // Create the communication record - for emails sent via system, start as 'draft' until confirmed sent
      const initialStatus: 'draft' | 'sent' | 'delivered' | 'failed' | 'logged' = asDraft 
        ? 'draft' 
        : (sendViaResend && channel === 'email' ? 'draft' : 'logged');
      
      const comm = await createCommunication({
        channel,
        direction: 'outbound',
        status: initialStatus,
        subject: channel === 'email' ? subject : undefined,
        body,
        recipient_email: channel === 'email' ? candidate.email : undefined,
        recipient_phone:
          channel === 'phone' || channel === 'whatsapp' || channel === 'sms'
            ? candidate.phone
            : undefined,
        job_id: job?.id,
        in_reply_to: replyTo?.originalMessageId,
      });

      if (!comm) return;

      // Upload attachments if any
      if (attachments.length > 0) {
        for (const file of attachments) {
          const filePath = `${candidateId}/${comm.id}/${file.name}`;
          const { error: uploadError } = await supabase.storage
            .from('communication-attachments')
            .upload(filePath, file);

          if (uploadError) {
            console.error('Error uploading attachment:', uploadError);
            continue;
          }

          await supabase.from('communication_attachments').insert({
            communication_id: comm.id,
            file_name: file.name,
            file_size: file.size,
            file_type: file.type,
            storage_path: filePath,
          });
        }
      }

      // If email channel and sendViaResend is enabled, actually send the email
      if (channel === 'email' && sendViaResend && !asDraft && candidate.email) {
        try {
          // Convert attachments to base64 for sending
          const attachmentData = await Promise.all(
            attachments.map(async (file) => {
              const arrayBuffer = await file.arrayBuffer();
              const base64 = btoa(
                new Uint8Array(arrayBuffer).reduce(
                  (data, byte) => data + String.fromCharCode(byte),
                  ''
                )
              );
              return {
                filename: file.name,
                content: base64,
                content_type: file.type,
              };
            })
          );

          const { data, error } = await supabase.functions.invoke('send-email', {
            body: {
              communicationId: comm.id,
              to: candidate.email,
              subject: subject || '(No Subject)',
              body: body.replace(/\n/g, '<br>'), // Convert newlines to HTML
              fromEmail: primaryConfig?.email_address,
              fromName: primaryConfig?.display_name,
              bccEmail: primaryConfig?.bcc_to_personal,
              attachments: attachmentData.length > 0 ? attachmentData : undefined,
            },
          });

          if (error) throw error;

          toast({
            title: 'Email sent',
            description: `Email sent successfully to ${candidate.email}`,
          });
        } catch (emailError: any) {
          console.error('Error sending email:', emailError);
          
          // Update communication status to failed
          await supabase
            .from('communications')
            .update({ status: 'failed' })
            .eq('id', comm.id);

          toast({
            title: 'Email failed',
            description: emailError.message || 'Failed to send email. It has been saved as a record.',
            variant: 'destructive',
          });
        }
      } else {
        toast({
          title: asDraft ? 'Draft saved' : 'Message logged',
          description: asDraft
            ? 'Your draft has been saved'
            : 'Communication has been recorded',
        });
      }

      onSent?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Error',
        description: 'Failed to save communication',
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{getChannelTitle()}</DialogTitle>
          <DialogDescription>
            {getChannelDescription()}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={mode} onValueChange={(v) => setMode(v as 'compose' | 'ai')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="compose">Compose</TabsTrigger>
            <TabsTrigger value="ai">
              <Sparkles className="h-4 w-4 mr-1.5" />
              AI Assist
            </TabsTrigger>
          </TabsList>

          <TabsContent value="compose" className="space-y-4 mt-4">
            {/* Template Selection */}
            {templates.length > 0 && (
              <div className="space-y-2">
                <Label>Template</Label>
                <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Subject (for emails) */}
            {channel === 'email' && (
              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter subject..."
                />
              </div>
            )}

            {/* Send via Resend toggle */}
            {channel === 'email' && primaryConfig && (
              <div className="flex items-center justify-between rounded-lg border p-3 bg-muted/30">
                <div className="space-y-0.5">
                  <Label htmlFor="send-via-resend" className="text-sm font-medium">
                    Send email via system
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    From: {primaryConfig.display_name || primaryConfig.email_address}
                    {primaryConfig.bcc_to_personal && (
                      <span className="ml-2">(BCC: {primaryConfig.bcc_to_personal})</span>
                    )}
                  </p>
                </div>
                <Switch
                  id="send-via-resend"
                  checked={sendViaResend}
                  onCheckedChange={setSendViaResend}
                />
              </div>
            )}

            {/* Body */}
            <div className="space-y-2">
              <Label htmlFor="body">
                {channel === 'note' ? 'Note' : 'Message'}
              </Label>
              {channel === 'email' ? (
                <RichTextEditor
                  value={body}
                  onChange={setBody}
                  placeholder="Type your message..."
                  minHeight="200px"
                />
              ) : (
                <Textarea
                  id="body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder={
                    channel === 'note'
                      ? 'Add your notes here...'
                      : 'Type your message...'
                  }
                  className="min-h-[200px]"
                />
              )}
            </div>

            {/* Attachments */}
            {channel === 'email' && (
              <div className="space-y-2">
                <Label>Attachments</Label>
                <div className="flex flex-wrap gap-2">
                  {attachments.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 bg-muted px-3 py-1.5 rounded-md text-sm"
                    >
                      <Paperclip className="h-3 w-3" />
                      <span className="truncate max-w-[150px]">{file.name}</span>
                      <button
                        onClick={() => removeAttachment(index)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <div className="flex items-center gap-2 px-3 py-1.5 border border-dashed rounded-md text-sm text-muted-foreground hover:text-foreground hover:border-foreground transition-colors">
                      <Paperclip className="h-3 w-3" />
                      Add file
                    </div>
                  </label>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="ai" className="space-y-4 mt-4">
            <div className="text-center py-8 space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
                <Sparkles className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">AI Draft Generator</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  AI will analyze the candidate's profile
                  {job && ' and job requirements'} to generate a personalized message.
                </p>
              </div>
              <Button onClick={generateAIDraft} disabled={generating}>
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Draft
                  </>
                )}
              </Button>
              {body && (
                <p className="text-sm text-green-600">
                  ✓ Draft generated! Switch to Compose tab to review and edit.
                </p>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="outline" onClick={() => handleSend(true)} disabled={sending}>
            <Save className="h-4 w-4 mr-1.5" />
            Save Draft
          </Button>
          <Button onClick={() => handleSend(false)} disabled={sending || !body.trim()}>
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-1.5" />
                {channel === 'note' ? 'Save Note' : 'Log & Send'}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
